"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

import { ConfirmExitModal } from "@/components/ui/confirm-exit-modal";
import type { Locale } from "@/i18n-config";
import { defineLocaleRecord } from "@/i18n-config";
import { buildCheckoutRedirectPath, requiresCreditsUnlock, resolvePaidCourseCtaState } from "@/lib/courses/credit-access";

type CourseAccessCtaProps = {
  lang: Locale;
  courseSlug: string;
  courseTitle: string;
  hasLessons: boolean;
  firstLessonSlug: string | null;
  access: "free" | "paid";
  isAuthenticated: boolean;
  isUnlocked: boolean;
  creditCostCredits: number | null;
  balanceCredits: number;
};

type CourseAccessCtaCopy = {
  getAccessToCourse: string;
  openCourse: string;
  buyCredits: string;
  buyMoreCredits: string;
  notEnoughCredits: string;
  notEnoughCreditsDescription: string;
  createAccountHelper: string;
  signInToStart: string;
  signInToContinue: string;
  signInToSeeBalance: string;
  startCourse: string;
  continueHint: string;
  coursePreparing: string;
  courseCost: string;
  currentBalance: string;
  creditsUnit: string;
  costNotSet: string;
  unlocking: string;
  unlockFailed: string;
  confirmTitle: string;
  confirmDescription: string;
  confirmCourseName: string;
  confirmCost: string;
  confirmAction: string;
  cancelAction: string;
};

const copyByLocale = defineLocaleRecord<CourseAccessCtaCopy>({
  en: {
    getAccessToCourse: "Get access to course",
    openCourse: "Open course",
    buyCredits: "Buy credits",
    buyMoreCredits: "Buy more credits",
    notEnoughCredits: "Not enough credits",
    notEnoughCreditsDescription: "This course requires more credits than are currently available.",
    createAccountHelper: "Create an account to unlock this course.",
    signInToStart: "Sign in to start",
    signInToContinue: "Sign in to continue",
    signInToSeeBalance: "Sign in to see your credit balance.",
    startCourse: "Start course",
    continueHint: "Continue when you are ready. Your progress is saved.",
    coursePreparing: "This course structure is being prepared.",
    courseCost: "Course cost",
    currentBalance: "Current balance",
    creditsUnit: "credits",
    costNotSet: "Course cost is being configured.",
    unlocking: "Processing...",
    unlockFailed: "Unable to unlock the course right now.",
    confirmTitle: "Confirm course access",
    confirmDescription: "Please confirm this credit deduction.",
    confirmCourseName: "Course",
    confirmCost: "Cost",
    confirmAction: "Confirm",
    cancelAction: "Cancel",
  },
  fr: {
    getAccessToCourse: "Obtenir l'acces au cours",
    openCourse: "Ouvrir le cours",
    buyCredits: "Acheter des credits",
    buyMoreCredits: "Acheter plus de credits",
    notEnoughCredits: "Credits insuffisants",
    notEnoughCreditsDescription: "Ce cours demande plus de credits que ceux disponibles.",
    createAccountHelper: "Creez un compte pour debloquer ce cours.",
    signInToStart: "Se connecter pour commencer",
    signInToContinue: "Se connecter pour continuer",
    signInToSeeBalance: "Connectez-vous pour voir votre solde de credits.",
    startCourse: "Commencer le cours",
    continueHint: "Continuez quand vous etes pret. Votre progression est conservee.",
    coursePreparing: "La structure de ce cours est en preparation.",
    courseCost: "Cout du cours",
    currentBalance: "Solde actuel",
    creditsUnit: "credits",
    costNotSet: "Le cout du cours est en cours de configuration.",
    unlocking: "Traitement...",
    unlockFailed: "Impossible de debloquer le cours pour le moment.",
    confirmTitle: "Confirmer l'acces au cours",
    confirmDescription: "Veuillez confirmer cette deduction de credits.",
    confirmCourseName: "Cours",
    confirmCost: "Cout",
    confirmAction: "Confirmer",
    cancelAction: "Annuler",
  },
  ru: {
    getAccessToCourse: "Получить доступ к курсу",
    openCourse: "Открыть курс",
    buyCredits: "Купить кредиты",
    buyMoreCredits: "Купить больше кредитов",
    notEnoughCredits: "Недостаточно кредитов",
    notEnoughCreditsDescription: "Для этого курса нужно больше кредитов, чем доступно сейчас.",
    createAccountHelper: "Создайте аккаунт, чтобы открыть этот курс.",
    signInToStart: "Войти, чтобы начать",
    signInToContinue: "Войти, чтобы продолжить",
    signInToSeeBalance: "Войдите, чтобы увидеть баланс кредитов.",
    startCourse: "Начать курс",
    continueHint: "Продолжайте, когда будете готовы. Прогресс сохранится.",
    coursePreparing: "Структура курса готовится.",
    courseCost: "Стоимость курса",
    currentBalance: "Текущий баланс",
    creditsUnit: "кредитов",
    costNotSet: "Стоимость курса настраивается.",
    unlocking: "Обработка...",
    unlockFailed: "Не удалось открыть курс сейчас.",
    confirmTitle: "Подтвердите доступ к курсу",
    confirmDescription: "Подтвердите списание кредитов.",
    confirmCourseName: "Курс",
    confirmCost: "Стоимость",
    confirmAction: "Подтвердить",
    cancelAction: "Отмена",
  },
  uk: {
    getAccessToCourse: "Отримати доступ до курсу",
    openCourse: "Вiдкрити курс",
    buyCredits: "Купити кредити",
    buyMoreCredits: "Купити бiльше кредитiв",
    notEnoughCredits: "Недостатньо кредитiв",
    notEnoughCreditsDescription: "Для цього курсу потрiбно бiльше кредитiв, нiж доступно зараз.",
    createAccountHelper: "Створiть акаунт, щоб вiдкрити цей курс.",
    signInToStart: "Увiйти, щоб почати",
    signInToContinue: "Увiйти, щоб продовжити",
    signInToSeeBalance: "Увiйдiть, щоб побачити баланс кредитiв.",
    startCourse: "Почати курс",
    continueHint: "Продовжуйте, коли будете готовi. Прогрес збережеться.",
    coursePreparing: "Структура курсу готується.",
    courseCost: "Вартiсть курсу",
    currentBalance: "Поточний баланс",
    creditsUnit: "кредитiв",
    costNotSet: "Вартiсть курсу налаштовується.",
    unlocking: "Обробка...",
    unlockFailed: "Не вдалося вiдкрити курс зараз.",
    confirmTitle: "Пiдтвердiть доступ до курсу",
    confirmDescription: "Пiдтвердiть списання кредитiв.",
    confirmCourseName: "Курс",
    confirmCost: "Вартiсть",
    confirmAction: "Пiдтвердити",
    cancelAction: "Скасувати",
  },
  de: {
    getAccessToCourse: "Zugang zum Kurs erhalten",
    openCourse: "Kurs offnen",
    buyCredits: "Credits kaufen",
    buyMoreCredits: "Mehr Credits kaufen",
    notEnoughCredits: "Nicht genug Credits",
    notEnoughCreditsDescription: "Dieser Kurs benoetigt mehr Credits als aktuell verfuegbar sind.",
    createAccountHelper: "Erstelle ein Konto, um diesen Kurs freizuschalten.",
    signInToStart: "Anmelden zum Starten",
    signInToContinue: "Anmelden zum Fortfahren",
    signInToSeeBalance: "Melde dich an, um dein Credit-Guthaben zu sehen.",
    startCourse: "Kurs starten",
    continueHint: "Mach weiter, wenn du bereit bist. Dein Fortschritt bleibt erhalten.",
    coursePreparing: "Die Kursstruktur wird vorbereitet.",
    courseCost: "Kurskosten",
    currentBalance: "Aktuelles Guthaben",
    creditsUnit: "Credits",
    costNotSet: "Kurskosten werden konfiguriert.",
    unlocking: "Wird verarbeitet...",
    unlockFailed: "Der Kurs kann gerade nicht freigeschaltet werden.",
    confirmTitle: "Kurszugang bestatigen",
    confirmDescription: "Bitte bestatige diese Credit-Belastung.",
    confirmCourseName: "Kurs",
    confirmCost: "Kosten",
    confirmAction: "Bestatigen",
    cancelAction: "Abbrechen",
  },
  es: {
    getAccessToCourse: "Obtener acceso al curso",
    openCourse: "Abrir curso",
    buyCredits: "Comprar creditos",
    buyMoreCredits: "Comprar mas creditos",
    notEnoughCredits: "No tienes suficientes creditos",
    notEnoughCreditsDescription: "Este curso requiere mas creditos de los disponibles.",
    createAccountHelper: "Crea una cuenta para desbloquear este curso.",
    signInToStart: "Inicia sesion para empezar",
    signInToContinue: "Inicia sesion para continuar",
    signInToSeeBalance: "Inicia sesion para ver tu saldo de creditos.",
    startCourse: "Comenzar curso",
    continueHint: "Continua cuando quieras. Tu progreso se guarda.",
    coursePreparing: "La estructura de este curso se esta preparando.",
    courseCost: "Costo del curso",
    currentBalance: "Saldo actual",
    creditsUnit: "creditos",
    costNotSet: "Se esta configurando el costo del curso.",
    unlocking: "Procesando...",
    unlockFailed: "No se puede desbloquear el curso ahora mismo.",
    confirmTitle: "Confirmar acceso al curso",
    confirmDescription: "Confirma este descuento de creditos.",
    confirmCourseName: "Curso",
    confirmCost: "Costo",
    confirmAction: "Confirmar",
    cancelAction: "Cancelar",
  },
  pt: {
    getAccessToCourse: "Obter acesso ao curso",
    openCourse: "Abrir curso",
    buyCredits: "Comprar creditos",
    buyMoreCredits: "Comprar mais creditos",
    notEnoughCredits: "Creditos insuficientes",
    notEnoughCreditsDescription: "Este curso exige mais creditos do que voce tem agora.",
    createAccountHelper: "Crie uma conta para desbloquear este curso.",
    signInToStart: "Entrar para comecar",
    signInToContinue: "Entrar para continuar",
    signInToSeeBalance: "Entre para ver seu saldo de creditos.",
    startCourse: "Comecar curso",
    continueHint: "Continue quando estiver pronto. Seu progresso fica salvo.",
    coursePreparing: "A estrutura deste curso esta sendo preparada.",
    courseCost: "Custo do curso",
    currentBalance: "Saldo atual",
    creditsUnit: "creditos",
    costNotSet: "O custo do curso esta sendo configurado.",
    unlocking: "Processando...",
    unlockFailed: "Nao foi possivel desbloquear o curso agora.",
    confirmTitle: "Confirmar acesso ao curso",
    confirmDescription: "Confirme esta deducao de creditos.",
    confirmCourseName: "Curso",
    confirmCost: "Custo",
    confirmAction: "Confirmar",
    cancelAction: "Cancelar",
  },
  it: {
    getAccessToCourse: "Ottieni accesso al corso",
    openCourse: "Apri corso",
    buyCredits: "Acquista crediti",
    buyMoreCredits: "Acquista piu crediti",
    notEnoughCredits: "Crediti insufficienti",
    notEnoughCreditsDescription: "Questo corso richiede piu crediti di quelli disponibili.",
    createAccountHelper: "Crea un account per sbloccare questo corso.",
    signInToStart: "Accedi per iniziare",
    signInToContinue: "Accedi per continuare",
    signInToSeeBalance: "Accedi per vedere il saldo crediti.",
    startCourse: "Inizia corso",
    continueHint: "Continua quando vuoi. I tuoi progressi sono salvati.",
    coursePreparing: "La struttura del corso e in preparazione.",
    courseCost: "Costo corso",
    currentBalance: "Saldo attuale",
    creditsUnit: "crediti",
    costNotSet: "Il costo del corso e in configurazione.",
    unlocking: "Elaborazione...",
    unlockFailed: "Impossibile sbloccare il corso ora.",
    confirmTitle: "Conferma accesso al corso",
    confirmDescription: "Conferma questo addebito in crediti.",
    confirmCourseName: "Corso",
    confirmCost: "Costo",
    confirmAction: "Conferma",
    cancelAction: "Annulla",
  },
});

function toSafeCredits(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.floor(value));
}

export function CourseAccessCta({
  lang,
  courseSlug,
  courseTitle,
  hasLessons,
  firstLessonSlug,
  access,
  isAuthenticated,
  isUnlocked,
  creditCostCredits,
  balanceCredits,
}: CourseAccessCtaProps) {
  const router = useRouter();
  const copy = copyByLocale[lang];
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [localUnlocked, setLocalUnlocked] = useState(isUnlocked);
  const [localBalanceCredits, setLocalBalanceCredits] = useState(toSafeCredits(balanceCredits));

  useEffect(() => {
    setLocalUnlocked(isUnlocked);
  }, [isUnlocked]);

  useEffect(() => {
    setLocalBalanceCredits(toSafeCredits(balanceCredits));
  }, [balanceCredits]);

  const returnTo = `/${lang}/courses/${courseSlug}`;
  const startHref = firstLessonSlug ? `/${lang}/lessons/${firstLessonSlug}` : null;
  const loginHref = `/${lang}/login?next=${encodeURIComponent(returnTo)}`;
  const requiresUnlock = requiresCreditsUnlock({
    access,
    courseCostCredits: creditCostCredits,
  });
  const paidCost =
    requiresUnlock && typeof creditCostCredits === "number" && creditCostCredits > 0 ? Math.floor(creditCostCredits) : null;
  const checkoutHref = paidCost
    ? buildCheckoutRedirectPath({
        lang,
        courseSlug,
        requiredCredits: paidCost,
      })
    : `/${lang}/checkout?returnTo=${encodeURIComponent(returnTo)}`;

  const paidState = useMemo(() => {
    if (!requiresUnlock || access !== "paid") return null;
    return resolvePaidCourseCtaState({
      isAuthenticated,
      isUnlocked: localUnlocked,
      hasLessons,
      balanceCredits: localBalanceCredits,
      courseCostCredits: paidCost ?? 0,
    });
  }, [access, hasLessons, isAuthenticated, localBalanceCredits, localUnlocked, paidCost, requiresUnlock]);

  const handleConfirmUnlock = async () => {
    if (isSubmitting || access !== "paid" || !requiresUnlock) return;

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const response = await fetch(`/api/courses/${courseSlug}/unlock`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const payload = (await response.json().catch(() => null)) as
        | {
            code?: string;
            balanceCredits?: number;
          }
        | null;

      if (response.ok) {
        setLocalUnlocked(true);
        if (typeof payload?.balanceCredits === "number") {
          setLocalBalanceCredits(toSafeCredits(payload.balanceCredits));
        }
        setIsConfirmOpen(false);
        router.refresh();
        return;
      }

      if (response.status === 409 && payload?.code === "INSUFFICIENT_CREDITS") {
        setIsConfirmOpen(false);
        router.push(checkoutHref);
        return;
      }

      setErrorMessage(copy.unlockFailed);
    } catch {
      setErrorMessage(copy.unlockFailed);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!requiresUnlock || access === "free") {
    if (!hasLessons) {
      return (
        <>
          <button
            type="button"
            disabled
            className="inline-flex w-full min-h-11 items-center justify-center rounded-lg border border-[#cab998] bg-[#e7dcc8] px-4 text-sm font-semibold text-[#7a6d57]"
          >
            {copy.startCourse}
          </button>
          <p className="text-xs text-[#596a64]">{copy.coursePreparing}</p>
        </>
      );
    }

    if (!isAuthenticated) {
      return (
        <>
          <Link
            href={loginHref}
            className="inline-flex w-full min-h-11 items-center justify-center rounded-lg border border-[#0f766e] bg-[#0f766e] px-4 text-sm font-semibold text-white outline-none transition-colors hover:bg-[#0d645c] focus-visible:ring-2 focus-visible:ring-[#0f766e]/30"
          >
            {copy.getAccessToCourse}
          </Link>
          <p className="text-xs text-[#596a64]">{copy.signInToContinue}</p>
        </>
      );
    }

    if (!localUnlocked) {
      return (
        <>
          <button
            type="button"
            onClick={async () => {
              if (isSubmitting) return;

              setIsSubmitting(true);
              setErrorMessage(null);

              try {
                const response = await fetch(`/api/courses/${courseSlug}/unlock`, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                });

                if (response.ok) {
                  setLocalUnlocked(true);
                  router.refresh();
                  return;
                }

                setErrorMessage(copy.unlockFailed);
              } catch {
                setErrorMessage(copy.unlockFailed);
              } finally {
                setIsSubmitting(false);
              }
            }}
            disabled={isSubmitting}
            className="inline-flex w-full min-h-11 items-center justify-center rounded-lg border border-[#0f766e] bg-[#0f766e] px-4 text-sm font-semibold text-white outline-none transition-colors hover:bg-[#0d645c] focus-visible:ring-2 focus-visible:ring-[#0f766e]/30 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSubmitting ? copy.unlocking : copy.getAccessToCourse}
          </button>
        </>
      );
    }

    return (
      <>
        <Link
          href={startHref ?? returnTo}
          className="inline-flex w-full min-h-11 items-center justify-center rounded-lg border border-[#0f766e] bg-[#0f766e] px-4 text-sm font-semibold text-white outline-none transition-colors hover:bg-[#0d645c] focus-visible:ring-2 focus-visible:ring-[#0f766e]/30"
        >
          {copy.startCourse}
        </Link>
        <p className="text-xs text-[#63756f]">{copy.continueHint}</p>
      </>
    );
  }

  return (
    <>
      {paidState?.action === "login" ? (
        <>
          <button
            type="button"
            disabled
            aria-disabled="true"
            className="inline-flex w-full min-h-11 items-center justify-center rounded-lg border border-[#cab998] bg-[#e7dcc8] px-4 text-sm font-semibold text-[#7a6d57]"
          >
            {copy.getAccessToCourse}
          </button>
          <p className="text-xs text-[#596a64]">{copy.createAccountHelper}</p>
        </>
      ) : null}

      {paidState?.action === "start" ? (
        <Link
          href={startHref ?? returnTo}
          className="inline-flex w-full min-h-11 items-center justify-center rounded-lg border border-[#0f766e] bg-[#0f766e] px-4 text-sm font-semibold text-white outline-none transition-colors hover:bg-[#0d645c] focus-visible:ring-2 focus-visible:ring-[#0f766e]/30"
        >
          {copy.openCourse}
        </Link>
      ) : null}

      {paidState?.action === "unlock" || paidState?.action === "buy_credits" ? (
        <>
          <button
            type="button"
            onClick={() => {
              setErrorMessage(null);
              setIsConfirmOpen(true);
            }}
            disabled={isSubmitting || !paidCost}
            className="inline-flex w-full min-h-11 items-center justify-center rounded-lg border border-[#0f766e] bg-[#0f766e] px-4 text-sm font-semibold text-white outline-none transition-colors hover:bg-[#0d645c] focus-visible:ring-2 focus-visible:ring-[#0f766e]/30 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {copy.getAccessToCourse}
          </button>
          <ConfirmExitModal
            isOpen={isConfirmOpen}
            title={paidState.action === "buy_credits" ? copy.notEnoughCredits : copy.confirmTitle}
            description={paidState.action === "buy_credits" ? copy.notEnoughCreditsDescription : copy.confirmDescription}
            details={
              <div className="space-y-1 rounded-lg border border-[#ded1bc] bg-[#fdf8ef] px-3 py-2">
                {paidState.action === "buy_credits" ? (
                  <p className="font-medium text-[#8d2647]">{copy.notEnoughCredits}</p>
                ) : (
                  <>
                    <p>
                      <span className="font-semibold text-[#2a3b35]">{copy.confirmCourseName}:</span> {courseTitle}
                    </p>
                    <p>
                      <span className="font-semibold text-[#2a3b35]">{copy.confirmCost}:</span>{" "}
                      {paidCost ? `${paidCost} ${copy.creditsUnit}` : "—"}
                    </p>
                  </>
                )}
              </div>
            }
            confirmLabel={paidState.action === "buy_credits" ? copy.buyMoreCredits : isSubmitting ? copy.unlocking : copy.confirmAction}
            cancelLabel={copy.cancelAction}
            onConfirm={paidState.action === "buy_credits" ? () => router.push(checkoutHref) : handleConfirmUnlock}
            onCancel={() => {
              if (isSubmitting) return;
              setIsConfirmOpen(false);
            }}
            isBusy={paidState.action === "buy_credits" ? false : isSubmitting}
            confirmIsPrimary
          />
        </>
      ) : null}

      {paidState?.action === "unavailable" ? (
        <>
          <button
            type="button"
            disabled
            className="inline-flex w-full min-h-11 items-center justify-center rounded-lg border border-[#cab998] bg-[#e7dcc8] px-4 text-sm font-semibold text-[#7a6d57]"
          >
            {copy.getAccessToCourse}
          </button>
          <p className="text-xs text-[#596a64]">{copy.coursePreparing}</p>
        </>
      ) : null}

      {errorMessage ? <p className="text-xs text-[#8d2647]">{errorMessage}</p> : null}
    </>
  );
}
